import React, { useCallback, useEffect, useState } from "react";
import {
  FaBuilding,
  FaPlus,
  FaRegEdit,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { FaMapLocationDot } from "react-icons/fa6";
import { MdDelete } from "react-icons/md";
import ReactPaginate from "react-paginate";
import { Modal } from "react-bootstrap";
import { Button, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  addBusinessUnit,
  addLocationsUnit,
  clearBusinessUnitToast,
  deleteBusinessUnit,
  deleteLocationsUnit,
  fetchBusinessUnits,
  fetchLocationsUnits,
  setBusinessUnitPage,
  setBusinessUnitSearch,
  setLocationsUnitPage,
  setLocationsUnitSearch,
  updateBusinessUnit,
  updateLocationsUnit,
} from "../store/reducers/businessUnitSlice";

const itemsPerPage = 10;

const entityConfig = {
  business: {
    title: "Business Units",
    singular: "business unit",
    nameLabel: "Business Unit Name",
    searchPlaceholder: "Search business units",
    emptyText: "No business units found",
    addTitle: "Add Business Unit",
    editTitle: "Edit Business Unit",
    icon: FaBuilding,
  },
  location: {
    title: "Locations",
    singular: "location",
    nameLabel: "Location Name",
    searchPlaceholder: "Search locations",
    emptyText: "No locations found",
    addTitle: "Add Location",
    editTitle: "Edit Location",
    icon: FaMapLocationDot,
  },
};

const getBusinessUnitName = (item) => item?.business_unit ||  "";

const getLocationName = (item) => item?.locations_units || "";

const canManageBusinessUnitsForRole = (role) => {
  const normalizedRole = `${role || ""}`.toLowerCase().replace(/[\s_-]/g, "");

  return normalizedRole === "owner" || normalizedRole === "superadmin";
};

const ManagementTable = ({
  type,
  rows,
  loading,
  search,
  page,
  total,
  totalPages,
  canManage,
  onSearchChange,
  onPageChange,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const config = entityConfig[type];
  const Icon = config.icon;
  const getName = type === "business" ? getBusinessUnitName : getLocationName;
  const idPrefix = type === "business" ? "B" : "L";
  const colSpan = canManage ? 4 : 2;

  return (
    <section className="min-w-0 rounded-2xl border border-admin-border bg-white p-4 shadow-admin md:p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[6px] bg-[#fff0f1] text-admin-primary">
              <Icon size={18} />
            </span>
            <h3 className="m-0 text-[18px] font-extrabold text-admin-text">
              {config.title}
            </h3>
          </div>
          <p className="mt-2 mb-0 text-sm text-admin-muted">
            Showing {rows.length} of {total} {config.title.toLowerCase()}
          </p>
        </div>

        {canManage && (
          <button
            type="button"
            className="admin-btn w-full whitespace-nowrap sm:w-auto"
            onClick={onAdd}
          >
            <FaPlus />
            Add New
          </button>
        )}
      </div>

      <div className="mb-4 flex min-h-[44px] w-full items-center gap-2.5 rounded-[10px] border border-gray-300 bg-white px-3 shadow-sm transition-colors focus-within:border-admin-primary">
        <FaSearch className="text-gray-400" />
        <input
          type="text"
          placeholder={config.searchPlaceholder}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent outline-none"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            className="border-0 bg-transparent text-gray-400 hover:text-admin-primary"
            aria-label={`Clear ${config.singular} search`}
          >
            <FaTimes />
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-admin-border shadow-sm">
        <div className="overflow-auto">
          <table className="admin-data-table m-0 w-full min-w-[520px]">
            <thead>
              <tr>
                <th className="sticky top-0 z-[1] h-14 whitespace-nowrap rounded-tl-xl bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white">
                  ID
                </th>
                <th
                  className={`sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white ${
                    canManage ? "" : "rounded-tr-xl"
                  }`}
                >
                  {config.nameLabel}
                </th>
                {canManage && (
                  <>
                    <th className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-center text-xs font-extrabold text-white">
                      Edit
                    </th>
                    <th className="sticky top-0 z-[1] h-14 whitespace-nowrap rounded-tr-xl bg-admin-primary px-4 py-3.5 text-center text-xs font-extrabold text-white">
                      Delete
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={colSpan}
                    className="px-4 py-8 text-center text-admin-muted"
                  >
                    <CircularProgress size={14} color="inherit" />
                  </td>
                </tr>
              ) : (
                rows.map((item, index) => (
                  <tr
                    key={item.id || `${getName(item)}-${index}`}
                    className="transition-colors duration-200 even:bg-[#fafafa] hover:bg-[#fff3f4]"
                  >
                    <th className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] font-normal text-admin-text align-middle">
                      {`${idPrefix}${
                        item.id || index + 1 + itemsPerPage * (page - 1)
                      }`}
                    </th>
                    <td className="min-w-[220px] border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                      <span className="block break-words">{getName(item)}</span>
                    </td>
                    {canManage && (
                      <>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-2 text-center text-[13px] text-admin-text align-middle">
                          <button
                            type="button"
                            className="inline-flex min-h-8 min-w-8 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-admin-primary transition-all duration-200 hover:-translate-y-px hover:text-admin-primary-hover"
                            aria-label={`Edit ${config.singular}`}
                            onClick={() => onEdit(item)}
                          >
                            <FaRegEdit size={20} />
                          </button>
                        </td>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-2 text-center text-[13px] text-admin-text align-middle">
                          <button
                            type="button"
                            className="inline-flex min-h-8 min-w-8 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-admin-danger transition-all duration-200 hover:-translate-y-px hover:text-red-600"
                            aria-label={`Delete ${config.singular}`}
                            onClick={() => onDelete(item.id)}
                          >
                            <MdDelete size={24} />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}

              <tr hidden={rows.length !== 0 || loading}>
                <td
                  colSpan={colSpan}
                  className="px-4 py-8 text-center text-admin-muted"
                >
                  {config.emptyText}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-end mt-3">
          <ReactPaginate
            nextLabel="Next"
            previousLabel="Previous"
            onPageChange={(event) => onPageChange(event.selected + 1)}
            pageCount={totalPages}
            forcePage={page - 1}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakLabel="..."
            breakClassName="page-item"
            breakLinkClassName="page-link"
            containerClassName="pagination flex-wrap"
            activeClassName="active"
            renderOnZeroPageCount={null}
          />
        </div>
      )}
    </section>
  );
};

const BusinessUnitLocation = () => {
  const dispatch = useDispatch();
  const organizationId = localStorage.getItem("organisation_id");
  const { organizations } = useSelector((state) => state.userOrganizations);
  const {
    items: businessUnits,
    loading: businessUnitLoading,
    saving,
    deleting,
    search: businessUnitSearch,
    page: businessUnitCurrentPage,
    totalPages: businessUnitPageCount,
    total: businessUnitTotalCount,
    locations,
    error,
    successMessage,
  } = useSelector((state) => state.businessUnits);
  const localRole = localStorage.getItem("user_role");
  const userType = localStorage.getItem("type");
  const organizationRole = (organizations || []).find(
    (org) => org.organisation_id === +organizationId
  )?.role;
  const canManageBusinessUnits =
    canManageBusinessUnitsForRole(localRole) ||
    canManageBusinessUnitsForRole(userType) ||
    canManageBusinessUnitsForRole(organizationRole);
  const [formName, setFormName] = useState("");
  const [formError, setFormError] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [modalType, setModalType] = useState("business");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchBusinessUnitList = useCallback(
    () =>
      dispatch(
        fetchBusinessUnits({
          page: businessUnitCurrentPage,
          limit: itemsPerPage,
          search: businessUnitSearch,
          organisationId: organizationId,
        })
      ),
    [businessUnitCurrentPage, businessUnitSearch, dispatch, organizationId]
  );

  const fetchLocationList = useCallback(
    () =>
      dispatch(
        fetchLocationsUnits({
          page: locations.page,
          limit: itemsPerPage,
          search: locations.search,
          organisationId: organizationId,
        })
      ),
    [dispatch, locations.page, locations.search, organizationId]
  );

  useEffect(() => {
    let businessUnitRequest;
    const timer = setTimeout(() => {
      businessUnitRequest = fetchBusinessUnitList();
    }, 500);

    return () => {
      clearTimeout(timer);
      businessUnitRequest?.abort();
    };
  }, [fetchBusinessUnitList]);

  useEffect(() => {
    let locationRequest;
    const timer = setTimeout(() => {
      locationRequest = fetchLocationList();
    }, 500);

    return () => {
      clearTimeout(timer);
      locationRequest?.abort();
    };
  }, [fetchLocationList]);

  const refreshBusinessUnitList = () =>
    dispatch(
      fetchBusinessUnits({
        page: businessUnitCurrentPage,
        limit: itemsPerPage,
        search: businessUnitSearch,
        organisationId: organizationId,
      })
    );

  const refreshLocationList = () =>
    dispatch(
      fetchLocationsUnits({
        page: locations.page,
        limit: itemsPerPage,
        search: locations.search,
        organisationId: organizationId,
      })
    );

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearBusinessUnitToast());
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearBusinessUnitToast());
    }
  }, [error, dispatch]);

  const openFormModal = (type, item = null) => {
    if (!canManageBusinessUnits) return;

    setModalType(type);
    setEditingItem(item);
    setFormError("");
    setFormName(
      item
        ? type === "business"
          ? getBusinessUnitName(item)
          : getLocationName(item)
        : ""
    );
  };

  const closeFormModal = () => {
    if (saving) return;
    setEditingItem(null);
    setFormName("");
    setFormError("");
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!canManageBusinessUnits) {
      toast.error("You do not have permission to manage this data");
      return;
    }

    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError(`${entityConfig[modalType].nameLabel} is required`);
      return;
    }

    const isEditing = Boolean(editingItem?.id);
    const payload =
      modalType === "business"
        ? {
            ...(isEditing ? { id: editingItem.id } : {}),
            ...(isEditing ? {} : { organisation_id: Number(organizationId) }),
            business_unit: trimmedName,
          }
        : {
            ...(isEditing ? { id: editingItem.id } : {}),
            ...(isEditing ? {} : { organisation_id: Number(organizationId) }),
            locations_units: trimmedName,
          };

    try {
      if (modalType === "business") {
        await dispatch(
          isEditing ? updateBusinessUnit(payload) : addBusinessUnit(payload)
        ).unwrap();
        refreshBusinessUnitList();
      } else {
        await dispatch(
          isEditing ? updateLocationsUnit(payload) : addLocationsUnit(payload)
        ).unwrap();
        refreshLocationList();
      }
      closeFormModal();
    } catch (requestError) {
      // Error toast is handled from Redux state.
    }
  };

  const handleDeleteClick = (type, id) => {
    if (!canManageBusinessUnits) return;
    setDeleteTarget({ type, id });
  };

  const handleConfirmDelete = async () => {
    if (!canManageBusinessUnits) {
      toast.error("You do not have permission to manage this data");
      return;
    }

    if (!deleteTarget?.id) return;

    try {
      if (deleteTarget.type === "business") {
        await dispatch(deleteBusinessUnit(deleteTarget.id)).unwrap();
        refreshBusinessUnitList();
      } else {
        await dispatch(deleteLocationsUnit(deleteTarget.id)).unwrap();
        refreshLocationList();
      }
      setDeleteTarget(null);
    } catch (requestError) {
      // Error toast is handled from Redux state.
    }
  };

  const activeConfig = entityConfig[modalType];

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-admin-border bg-white p-4 shadow-admin md:flex-row md:items-center md:justify-between md:p-6">
        <div>
          <h2 className="m-0 text-[22px] font-extrabold text-admin-text">
            Location & Business Unit
          </h2>
          <p className="mt-1 mb-0 text-sm text-admin-muted">
            Manage business units and locations separately for this
            organization.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm sm:flex">
          <div className="rounded-[6px] border border-admin-border px-3 py-2">
            <span className="block text-xs text-admin-muted">
              Business Units
            </span>
            <strong className="text-admin-text">
              {businessUnitTotalCount}
            </strong>
          </div>
          <div className="rounded-[6px] border border-admin-border px-3 py-2">
            <span className="block text-xs text-admin-muted">Locations</span>
            <strong className="text-admin-text">{locations.total}</strong>
          </div>
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
        <ManagementTable
          type="business"
          rows={businessUnits}
          loading={businessUnitLoading}
          search={businessUnitSearch}
          page={businessUnitCurrentPage}
          total={businessUnitTotalCount}
          totalPages={businessUnitPageCount}
          canManage={canManageBusinessUnits}
          onSearchChange={(value) => dispatch(setBusinessUnitSearch(value))}
          onPageChange={(page) => dispatch(setBusinessUnitPage(page))}
          onAdd={() => {
            setModalType("business");
            setEditingItem(false);
            setFormName("");
            setFormError("");
          }}
          onEdit={(item) => openFormModal("business", item)}
          onDelete={(id) => handleDeleteClick("business", id)}
        />

        <ManagementTable
          type="location"
          rows={locations.items}
          loading={locations.loading}
          search={locations.search}
          page={locations.page}
          total={locations.total}
          totalPages={locations.totalPages}
          canManage={canManageBusinessUnits}
          onSearchChange={(value) => dispatch(setLocationsUnitSearch(value))}
          onPageChange={(page) => dispatch(setLocationsUnitPage(page))}
          onAdd={() => {
            setModalType("location");
            setEditingItem(false);
            setFormName("");
            setFormError("");
          }}
          onEdit={(item) => openFormModal("location", item)}
          onDelete={(id) => handleDeleteClick("location", id)}
        />
      </div>

      {canManageBusinessUnits && editingItem !== null && (
        <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-black/50 px-3">
          <div className="w-full max-w-md rounded-[6px] bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h5 className="m-0 text-base font-semibold text-gray-900">
                {editingItem ? activeConfig.editTitle : activeConfig.addTitle}
              </h5>
              <button
                type="button"
                onClick={closeFormModal}
                disabled={saving}
                aria-label="Close"
                className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-black bg-white p-0 hover:bg-gray-100"
              >
                <FaTimes size={14} />
              </button>
            </div>

            <form onSubmit={handleFormSubmit}>
              <div className="space-y-3 px-4 py-3">
                {formError && (
                  <div className="rounded-[4px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {formError}
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {activeConfig.nameLabel}
                  </label>
                  <input
                    type="text"
                    className="form-control h-auto rounded-[4px] px-3 py-2 text-sm"
                    value={formName}
                    onChange={(event) => {
                      setFormError("");
                      setFormName(event.target.value);
                    }}
                    placeholder={`Enter ${activeConfig.singular} name`}
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary px-3"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-sm bg_color px-3"
                  disabled={saving}
                >
                  {saving ? "Saving..." : editingItem ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Modal
        show={canManageBusinessUnits && Boolean(deleteTarget)}
        onHide={() => setDeleteTarget(null)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="px-3 py-2">
          <Modal.Title className="text-base">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-3 py-3">
          Are you sure you want to delete this{" "}
          {deleteTarget ? entityConfig[deleteTarget.type].singular : "item"}?
        </Modal.Body>
        <Modal.Footer className="px-3 py-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDeleteTarget(null)}
          >
            No
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="bg_color border-0"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Yes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BusinessUnitLocation;
